import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { GeoJSON } from 'geojson';
import { BigQueryTable, Coordinates, JourneyId, MileMarker, SessionId, TrajectoryHeatmapId } from '../../util/types';
import { Mile, Milliseconds } from '../../util/units';

const dateFormat = 'YYYY-MM-DD';

interface TrajectorySliceState {
  startDate: string;
  endDate: string;
  dateRangeId: number;
  routeId: number | null;
  routeCreatorEmail: string | null;
  roadWidth: number;
  mileMarkerOffset: number;
  accelThreshold: number;
  subtractMileMarkers: boolean;
  flipMileMarkers: boolean;
  maxTravelTime: number | null;
  travelTimes: number[];
  showCfdChart: boolean;
  waypoints: string | null;
  route: GeoJSON | null;
  visibleCoordinates: [] | null;
  hoverMarkerPosition: MileMarker | null;
  distanceBetweenPoints: Mile;
  isChangingRoadWidth: boolean;
  hoveredTrajectoryJourneyId: JourneyId | null;
  submitClicked: boolean;
  submitFromURL: boolean;
  loadedNewHeatmap: boolean;
  routeName: string;
  role: string | null;
  sessionId: SessionId | null;
  organizationName: string | null;
  heatmapId: TrajectoryHeatmapId | null;
  eventTable: BigQueryTable | null;
  movementTable: BigQueryTable | null;
  truckTable: BigQueryTable | null;
  userEmail: string | null;
  showOnlyPersonalRoutes: boolean;
  showSplitLines: boolean;
  slideshowSpeed: Milliseconds;
  slideshowSaveScreenshots: boolean;
  slideshowScreenshotFileName: string;
  slideshowRunning: boolean;
  freeFlowTravelTime: number | null;
  mapCenter: Coordinates;
  mapZoom: number;
  isLiveModeEnabled: boolean;
  totalTrajectories: number;
  totalPoints: number;
  totalHardBrakingEvents: number;
  totalHardAccelerationEvents: number;
  totalTravelTimePoints: number;
  maxDistanceFromEnds: number;
  includeTrucks: boolean;
}

const initialState: TrajectorySliceState = {
  startDate: dayjs().subtract(1, 'days').format(dateFormat),
  endDate: dayjs().format(dateFormat),
  dateRangeId: 1,
  routeId: null,
  routeCreatorEmail: null,
  roadWidth: 100,
  mileMarkerOffset: 0,
  accelThreshold: 0.25,
  subtractMileMarkers: false,
  flipMileMarkers: false,
  maxTravelTime: null,
  travelTimes: [],
  showCfdChart: false,
  // Once this becomes true, it will stay true.
  // loadedMaxTravelTime: false,
  waypoints: null,
  route: null,
  // The coordinates of the route that are visible. Used to sync map and plot.
  // Null means ignore, [] means show original bounds, and [...] means to show these coordinates
  visibleCoordinates: null,
  // This is the mile marker where the hover marker is. Null if it shouldn't be displayed
  hoverMarkerPosition: null,
  distanceBetweenPoints: 0.01 as Mile,
  isChangingRoadWidth: false,
  hoveredTrajectoryJourneyId: null,
  submitClicked: false,
  submitFromURL: false,
  loadedNewHeatmap: false,
  routeName: '',
  role: null,
  sessionId: null,
  organizationName: null,
  heatmapId: null,
  eventTable: null,
  movementTable: null,
  truckTable: null,
  userEmail: null,
  showOnlyPersonalRoutes: false,
  showSplitLines: (localStorage.getItem('showSplitLines')) ?
    localStorage.getItem('showSplitLines') === 'true' : true,
  slideshowSpeed: (parseInt(localStorage.getItem('slideshowSpeed') as string, 10) || 1000) as Milliseconds,
  slideshowSaveScreenshots: localStorage.getItem('slideshowSaveScreenshots') === 'true',
  slideshowScreenshotFileName: localStorage.getItem('slideshowScreenshotFileName') || 'slideshow_screenshot',
  slideshowRunning: false,
  freeFlowTravelTime: null,
  mapCenter: { lat: 39.801804, lng: -86.156207 },
  mapZoom: 9,
  isLiveModeEnabled: localStorage.getItem('isLiveModeEnabled') === 'true',
  totalTrajectories: 0,
  totalPoints: 0,
  totalHardBrakingEvents: 0,
  totalHardAccelerationEvents: 0,
  totalTravelTimePoints: 0,
  maxDistanceFromEnds: parseInt(localStorage.getItem('maxDistanceFromEnds') as string) || 0.25,
  includeTrucks: false,
};

export const trajectorySlice = createSlice({
  name: 'trajectory',
  initialState,
  reducers: {
    setTrajectoryStore: (trajectory: { [key: string]: unknown }, action) => {
      for (const key in action.payload) {
        if (action.payload.hasOwnProperty(key)) trajectory[key] = action.payload[key];
      }
    },
  },
});

export const {
  setTrajectoryStore,
} = trajectorySlice.actions;
export default trajectorySlice.reducer;
